import React, {useEffect} from "react";
import {useStores} from "../../../stores";
import {Button, Grid, Typography} from "@mui/material";
import {CurrencyComponent} from "../../../components/currency-component/CurrencyComponent";
import './KoiPurchasePriceVariance.scss';
import {PurchasePriceVarianceHelp} from "./components/PurchasePriceVarianceHelp";
import {generatePath, useNavigate} from "react-router";
import {routes} from "../../../routing/routes";
import {observer} from "mobx-react-lite";
import {SingleBarChart} from "../../../components/visualization/single-barchart/SingleBarChart";
import {UNCATEGORIZED_VALUE} from "../../../constants";
import {NO_MARGIN} from "../../../utils/margin";
import {PpvExpandableTable} from "./components/PpvExpandableTable";
import {PpvBreadcrumbs} from "./components/PpvBreadcrumbs";
import {ShareModal} from "../../../pages/business-case-generator/components/modals/ShareModal";
import {PpvEditNoteModal} from "./modals/PpvEditNoteModal";
import {runInAction} from "mobx";
import {PpvDeleteGroupModal} from "./modals/PpvDeleteGroupModal";
import {PpvFilterBarComponent} from "./components/PpvFilterBarComponent";
import {MainPageFacade} from "../../../components/main/page/MainPageFacade.tsx";
import type {KpiBoxedValue} from "../../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import {KpiBoxedValues} from "../../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import {CARD_CONFIG} from "../../../components/dashboard/DashboardCardConfigs.tsx";

const EXTEND_NAVIGATION_THRESHOLD = 3;

export const KOIPurchasePriceVariance: React.FC = observer(() => {
    const {bagStore, ppvControllerStore} = useStores();
    const bagId = bagStore.bagId;
    const navigate = useNavigate();
    const [isShareOpen, setIsShareOpen] = React.useState(false);

    const rootPpv = ppvControllerStore.ppvPageController.ppvPageStatisticsController.rootPpv;
    const rootSpend = ppvControllerStore.ppvPageController.ppvPageStatisticsController.rootSpend;
    const rootSuppliers = ppvControllerStore.ppvPageController.ppvPageStatisticsController.rootSuppliers;

    const taxonomySize = bagStore.taxonomy_size;
    useEffect(() => {
        ppvControllerStore.init(bagId, taxonomySize)
    }, [ppvControllerStore, bagId, taxonomySize])

    useEffect(() => {
        ppvControllerStore.ppvPageController.loadData(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [isExtended, setIsExtended] = React.useState<boolean>(false);
    const dataMax = ppvControllerStore.ppvPageController.selectionCharts?.max || 1;

    // TODO: PR #98 Model View Controller mismatch + Underloaded MOBX Action
    const handleNoteUpdate = (updatedNote: string) => {
        runInAction(() => {
            if (ppvControllerStore.ppvRowToEdit && ppvControllerStore.ppvRowToEdit.data.opportunity) {
                ppvControllerStore.ppvRowToEdit.data.opportunity.note = updatedNote
            }
        })
    };

    const kpiValues = React.useMemo<KpiBoxedValue[]>(() => [
        {
            title: 'Suppliers in Scope',
            subtitle: rootSuppliers !== null ? `${rootSuppliers}` : <>&nbsp;</>
        }, {
            title: 'Total Purchase Price Variance',
            subtitle: rootPpv !== null ? <CurrencyComponent v={rootPpv} nDigits={3}/> : <>&nbsp;</>
        }, {
            title: 'Spend in Scope',
            subtitle: rootSpend !== null ? <CurrencyComponent v={rootSpend} nDigits={3}/> : <>&nbsp;</>,
        }, {
            title: 'Opportunity In Review',
            subtitle: ppvControllerStore.totalExpectedSavings != null
                ? <CurrencyComponent v={ppvControllerStore.totalExpectedSavings} nDigits={3}/>
                : <>&nbsp;</>,
        },
    ], [rootSuppliers, rootPpv, rootSpend, ppvControllerStore.totalExpectedSavings]);

    const selectionCharts = ppvControllerStore.ppvPageController.selectionCharts;
    const canExtend = selectionCharts ? selectionCharts.data.length > EXTEND_NAVIGATION_THRESHOLD : false;

    return (
        <MainPageFacade
            title={CARD_CONFIG['koi-ppv'].title}
            helpContent={<PurchasePriceVarianceHelp/>}
            //TODO: The share button is temporarily disable since we do not have a design for the BE
            // next={{
            //     type: 'component',
            //     node: <Button
            //         color="primary"
            //         variant="outlined"
            //         startIcon={<Share/>}
            //         onClick={() => setIsShareOpen(true)}>
            //         Share
            //     </Button>
            // }}
            next={{
                type: 'component',
                node: <Button
                    color="primary"
                    variant="outlined"
                    onClick={() => navigate(generatePath(routes.analytics_koi_ppv_opportunities, {id: bagStore.bagId}))}>
                    Go to opportunities
                </Button>
            }}
            className="koi-ppv-page"
        >
            <ShareModal isOpen={isShareOpen} onClose={() => setIsShareOpen(false)}/>

            {/*FIXME: @Dennis, I do not like how I implemented it but it will work. I think you will catch this in the PR*/}
            <PpvEditNoteModal onNoteUpdate={handleNoteUpdate}/>

            <PpvDeleteGroupModal/>

            {/*{ppvControllerStore.ppvDataDoesNotExist &&*/}
            {/*    <Grid container justifyContent="space-evenly">*/}
            {/*        <Grid style={{margin: '4em'}}>*/}
            {/*            <Alert severity="warning">*/}
            {/*                This dataset is missing data to calculate the Purchase Price Variance.*/}
            {/*            </Alert>*/}
            {/*        </Grid>*/}
            {/*    </Grid>*/}
            {/*}*/}

            <PpvBreadcrumbs/>

            <KpiBoxedValues values={kpiValues}/>

            {selectionCharts &&
                <Grid container className="overview-bar-charts">
                    <Grid size={12}>
                        {/* The bars of the bar chart */}
                        {selectionCharts.data.slice(0, isExtended ? undefined : 3).map(changeChartData => (
                            <div key={changeChartData.category}
                                 className={`clickable bar-chart-container l${ppvControllerStore.ppvPageController.ppvPageFilterController.selectedLevel + 1}`}
                                 onClick={() => ppvControllerStore.ppvPageController.ppvPageFilterController.selectNextCategoryDown(changeChartData.category)}>
                                <SingleBarChart
                                    key={changeChartData.category}
                                    data={{
                                        mainLabel: changeChartData.label,
                                        values: changeChartData.values,
                                    }}
                                    className={changeChartData.category === UNCATEGORIZED_VALUE ? 'uncat' : ''}
                                    options={{
                                        width: 800,
                                        height: 45 / 2,
                                        margin: NO_MARGIN,
                                        dataMax,
                                        labelHover: true
                                    }}
                                />
                            </div>
                        ))}
                    </Grid>
                    <Grid className={'text-center'} alignItems={'center'} size={12}>
                        {canExtend && <Typography
                            variant="h6"
                            className="clickable show-more-less"
                            onClick={() => setIsExtended(!isExtended)}
                            sx={{margin: '.5em'}}>
                            {isExtended ? 'Show less' : 'Show more'}
                        </Typography>}
                    </Grid>
                </Grid>
            }

            <PpvFilterBarComponent/>

            <section className="page-item ppv-table-section">
                <Grid container justifyContent="space-evenly">
                    <Grid style={{minHeight: '10em'}} size={12}>
                        <PpvExpandableTable/>
                    </Grid>
                </Grid>
            </section>
        </MainPageFacade>
    );
});
